import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Copy, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { SESSION_LABELS, ScenarioGrid } from './ScenarioMapModal';
import './ScenarioTemplateEditor.css';

export const POSITIONS = ['CB', 'FB', 'CDM', 'CAM', 'ST', 'W'];
export const METRICS   = ['TD', 'RD', 'HID', 'SD', 'SA', 'Expl', 'HMLD'];

const ALL_DAY_LABELS = [
  'MD+4', 'MD+3', 'MD+2', 'MD+1', 'MD', 'MD-1', 'MD-2', 'MD-3', 'MD-4', 'MD-5', 'MD-6', 'MD-7',
];

export function emptyDayTargets() {
  const t = {};
  POSITIONS.forEach(pos => {
    t[pos] = {};
    METRICS.forEach(m => { t[pos][m] = { upper: 0, lower: 0 }; });
  });
  return t;
}

// Scale a full position/metric target table by a factor (e.g. 0.5 for a 45-min match
// vs. the 90-min baseline) — used by the MD-day duration selector.
function scaleTargets(table, factor) {
  const out = {};
  POSITIONS.forEach(pos => {
    out[pos] = {};
    METRICS.forEach(m => {
      const cell = table?.[pos]?.[m] || { upper: 0, lower: 0 };
      out[pos][m] = { upper: Math.round(cell.upper * factor), lower: Math.round(cell.lower * factor) };
    });
  });
  return out;
}

// ── Metrics table for one day ────────────────────────────────────────────────
export function DayMetricsTable({ targets, onChange, readOnly }) {
  const handleChange = (pos, metric, bound, val) => {
    const v = parseFloat(val) || 0;
    onChange(pos, metric, bound, v);
  };

  return (
    <div className="ste-day-table-wrap">
      <table className="ste-metrics-table">
        <thead>
          <tr>
            <th className="ste-pos-col">Pos</th>
            {METRICS.map(m => (
              <th key={m} colSpan={2} className="ste-metric-head">{m}</th>
            ))}
          </tr>
          <tr>
            <th />
            {METRICS.map(m => (
              <React.Fragment key={m}>
                <th className="ste-bound-head">Upper</th>
                <th className="ste-bound-head">Lower</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {POSITIONS.map(pos => (
            <tr key={pos}>
              <td className="ste-pos-label">{pos}</td>
              {METRICS.map(m => (
                <React.Fragment key={m}>
                  <td className="ste-val-cell">
                    {readOnly
                      ? <span className="ste-val-ro">{targets?.[pos]?.[m]?.upper ?? 0}</span>
                      : <input
                          type="number"
                          className="ste-val-input"
                          value={targets?.[pos]?.[m]?.upper ?? 0}
                          onChange={e => handleChange(pos, m, 'upper', e.target.value)}
                          min={0}
                        />
                    }
                  </td>
                  <td className="ste-val-cell">
                    {readOnly
                      ? <span className="ste-val-ro">{targets?.[pos]?.[m]?.lower ?? 0}</span>
                      : <input
                          type="number"
                          className="ste-val-input"
                          value={targets?.[pos]?.[m]?.lower ?? 0}
                          onChange={e => handleChange(pos, m, 'lower', e.target.value)}
                          min={0}
                        />
                    }
                  </td>
                </React.Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Builder slot row ─────────────────────────────────────────────────────────
function BuilderSlot({ slot, index, allBuiltInDays, allTemplates, customTargets, onChange, onRemove }) {
  const sourceDays = allBuiltInDays[slot.sourceScenario] || [];

  return (
    <div className="ste-builder-slot">
      <span className="ste-slot-num">Day {index + 1}</span>

      <div className="ste-slot-field">
        <label>Day label</label>
        <div className="ste-select-wrap">
          <select
            value={slot.dayLabel}
            onChange={e => onChange(index, 'dayLabel', e.target.value)}
          >
            {ALL_DAY_LABELS.map(d => <option key={d}>{d}</option>)}
          </select>
          <ChevronDown size={13} className="ste-select-icon" />
        </div>
      </div>

      <div className="ste-slot-field">
        <label>Copy targets from</label>
        <div className="ste-select-wrap">
          <select
            value={slot.sourceScenario}
            onChange={e => onChange(index, 'sourceScenario', e.target.value)}
          >
            {allTemplates.map(s => <option key={s}>{s}</option>)}
          </select>
          <ChevronDown size={13} className="ste-select-icon" />
        </div>
      </div>

      <div className="ste-slot-field">
        <label>Source day</label>
        <div className="ste-select-wrap">
          <select
            value={slot.sourceDay}
            onChange={e => onChange(index, 'sourceDay', e.target.value)}
          >
            {sourceDays.map(d => <option key={d}>{d}</option>)}
          </select>
          <ChevronDown size={13} className="ste-select-icon" />
        </div>
      </div>

      <button className="ste-remove-slot" onClick={() => onRemove(index)} title="Remove slot">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

// ── Main editor ──────────────────────────────────────────────────────────────
export default function ScenarioTemplateEditor({
  editing,           // null = create new; object = edit existing
  allBuiltInTargets, // { [scenarioName]: { [day]: { [pos]: { [metric]: {upper,lower} } } } } — built-in + custom, despite the name
  allBuiltInDays,    // { [scenarioName]: [dayLabels] } — built-in + custom, despite the name
  allBuiltInLabels = SESSION_LABELS, // { [scenarioName]: { [day]: labelText } } — built-in + custom; defaults to built-in-only if a caller doesn't pass it
  allScenarioNames,  // all known scenario names (built-in + custom)
  onSave,
  onClose,
}) {
  const [name, setName]   = useState(editing?.name || '');
  const [mode, setMode]   = useState(editing?.slots?.length ? 'builder' : 'manual');
  const [showReference, setShowReference] = useState(false);
  const [activeDayIdx, setActiveDayIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // Manual mode state
  const [days, setDays]       = useState(editing?.days || ['MD+1', 'MD-1', 'MD']);
  // Per-day label ("Vrij", "Scherp", etc.) carried along whenever a day's targets are copied from
  // an existing scenario via the "Copy from" chips, so the View tab (ScenarioMapModal) can show
  // the same color/note it shows for the source scenario. Builder mode doesn't need this state —
  // its labels are derived straight from builderSlots at save time (every builder day already has
  // an explicit source scenario/day), see handleSave below.
  const [dayLabels, setDayLabels] = useState(editing?.labels || {});
  const [targets, setTargets] = useState(() => {
    if (editing?.targets) {
      const t = JSON.parse(JSON.stringify(editing.targets));
      delete t._mdMinutes;
      const mdMins = editing.targets._mdMinutes ?? 90;
      if (mdMins !== 90 && t.MD) t.MD = scaleTargets(t.MD, mdMins / 90);
      return t;
    }
    const t = {};
    (editing?.days || ['MD+1', 'MD-1', 'MD']).forEach(d => { t[d] = emptyDayTargets(); });
    return t;
  });

  // Builder mode state — restore from stored slots if available, otherwise use defaults
  const [builderSlots, setBuilderSlots] = useState(() => {
    if (editing?.slots?.length) return editing.slots;
    return (editing?.days || []).map(d => ({
      dayLabel:       d,
      sourceScenario: allScenarioNames[0] || '',
      sourceDay:      (allBuiltInDays[allScenarioNames[0]] || [])[0] || '',
    }));
  });

  // MD-day duration scaling — stored as _mdMinutes in the template's targets JSONB.
  // The template always stores 90-min base targets; GPSUpload scales at display time.
  const storedMdMins = editing?.targets?._mdMinutes ?? 90;
  const [mdMinutes, setMdMinutes] = useState(storedMdMins);
  const [mdBaseline, setMdBaseline] = useState(() => {
    if (editing?.targets?.MD && storedMdMins !== 90) {
      return JSON.parse(JSON.stringify(editing.targets.MD));
    }
    return null;
  });

  // Derived: the string label of the currently active day
  const activeDay = activeDayIdx < days.length ? days[activeDayIdx] : null;

  useEffect(() => {
    if (activeDayIdx >= days.length && days.length > 0) setActiveDayIdx(0);
  }, [days, activeDayIdx]);

  // ── Manual helpers ──────────────────────────────────────────────────────────

  const addDay = () => {
    const unused = ALL_DAY_LABELS.find(d => !days.includes(d));
    if (!unused) return;
    const newDays = [...days, unused];
    setDays(newDays);
    setTargets(prev => ({ ...prev, [unused]: emptyDayTargets() }));
    setActiveDayIdx(newDays.length - 1);
  };

  const removeDay = (dayLabel, idx) => {
    const newDays = days.filter((_, i) => i !== idx);
    setDays(newDays);
    setTargets(prev => {
      // Only remove targets entry if no other day still uses this label
      if (newDays.includes(dayLabel)) return prev;
      const t = { ...prev }; delete t[dayLabel]; return t;
    });
    setDayLabels(prev => {
      if (newDays.includes(dayLabel)) return prev;
      const l = { ...prev }; delete l[dayLabel]; return l;
    });
    if (dayLabel === 'MD' && !newDays.includes('MD')) { setMdMinutes(90); setMdBaseline(null); }
    setActiveDayIdx(Math.max(0, Math.min(activeDayIdx, newDays.length - 1)));
  };

  const updateTarget = (dayLabel, pos, metric, bound, value) => {
    setTargets(prev => ({
      ...prev,
      [dayLabel]: {
        ...prev[dayLabel],
        [pos]: {
          ...prev[dayLabel]?.[pos],
          [metric]: {
            ...prev[dayLabel]?.[pos]?.[metric],
            [bound]: value,
          },
        },
      },
    }));
  };

  const copyFromScenario = (dayLabel, srcScenario, srcDay) => {
    const src = allBuiltInTargets?.[srcScenario]?.[srcDay];
    if (!src) return;
    const cloned = JSON.parse(JSON.stringify(src));
    if (dayLabel === 'MD') {
      setMdBaseline(cloned);
      setTargets(prev => ({ ...prev, MD: scaleTargets(cloned, mdMinutes / 90) }));
    } else {
      setTargets(prev => ({ ...prev, [dayLabel]: cloned }));
    }
    setDayLabels(prev => ({ ...prev, [dayLabel]: allBuiltInLabels?.[srcScenario]?.[srcDay] ?? '' }));
  };

  // MD day is already baselined at 90 mins — first time the duration is changed,
  // lock in whatever's currently shown as that baseline so later changes don't
  // compound (45 → 60 → 45 should always land back on the same numbers).
  const handleMdMinutesChange = (mins) => {
    setMdMinutes(mins);
    const baseline = mdBaseline || targets.MD;
    if (!baseline) return;
    if (!mdBaseline) setMdBaseline(JSON.parse(JSON.stringify(baseline)));
    setTargets(prev => ({ ...prev, MD: scaleTargets(baseline, mins / 90) }));
  };

  // ── Builder helpers ─────────────────────────────────────────────────────────

  const addBuilderSlot = () => {
    const firstScenario = allScenarioNames[0] || '';
    const firstDay      = (allBuiltInDays[firstScenario] || [])[0] || '';
    const nextLabel     = ALL_DAY_LABELS.find(d => !builderSlots.some(s => s.dayLabel === d)) || 'MD';
    setBuilderSlots(prev => [
      ...prev,
      { dayLabel: nextLabel, sourceScenario: firstScenario, sourceDay: firstDay },
    ]);
  };

  const updateSlot = (index, field, value) => {
    setBuilderSlots(prev => {
      // Apply the change to the target slot
      const next = prev.map((s, i) => {
        if (i !== index) return s;
        const updated = { ...s, [field]: value };
        if (field === 'sourceScenario') {
          updated.sourceDay = (allBuiltInDays[value] || [])[0] || '';
        }
        return updated;
      });
      // Sync all slots sharing the same dayLabel — they map to the same targets entry
      const changed = next[index];
      return next.map((s, i) => {
        if (i === index || s.dayLabel !== changed.dayLabel) return s;
        return { ...s, sourceScenario: changed.sourceScenario, sourceDay: changed.sourceDay };
      });
    });
  };

  const removeSlot = index =>
    setBuilderSlots(prev => prev.filter((_, i) => i !== index));

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!name.trim()) return setError('Scenario name is required.');

    let finalDays, finalTargets, finalLabels;

    if (mode === 'manual') {
      if (days.length === 0) return setError('Add at least one day.');
      finalDays = days;
      // Always store the 90-min base for MD (mdBaseline if the user scaled,
      // otherwise targets.MD itself which is already at 90 min). GPSUpload
      // reads _mdMinutes and applies (mins/90)*1.1 at planning display time.
      const baseTargets = { ...targets };
      if (days.includes('MD') && mdBaseline) {
        baseTargets.MD = JSON.parse(JSON.stringify(mdBaseline));
      }
      finalTargets = { ...baseTargets, _mdMinutes: mdMinutes };
      // Drop any label left over from a day that's since been removed.
      finalLabels = Object.fromEntries(Object.entries(dayLabels).filter(([d]) => days.includes(d)));
    } else {
      if (builderSlots.length === 0) return setError('Add at least one day slot.');
      finalDays    = builderSlots.map(s => s.dayLabel);
      finalTargets = {};
      finalLabels  = {};
      // Process in reverse so the LAST slot for a given dayLabel wins — the most recently
      // added slot is the user's most recent intent for that label. Labels follow the exact
      // same precedence as targets so a re-used day label never ends up with a target/label
      // mismatch (one sourced from a different slot than the other).
      [...builderSlots].reverse().forEach(slot => {
        const src = allBuiltInTargets?.[slot.sourceScenario]?.[slot.sourceDay];
        finalTargets[slot.dayLabel] = src
          ? JSON.parse(JSON.stringify(src))
          : emptyDayTargets();
        finalLabels[slot.dayLabel] = allBuiltInLabels?.[slot.sourceScenario]?.[slot.sourceDay] ?? '';
      });
      finalTargets._mdMinutes = mdMinutes;
    }

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        days: finalDays,
        targets: finalTargets,
        slots: mode === 'builder' ? builderSlots : null,
        labels: finalLabels,
      });
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="ste-overlay">
      <div className="ste-modal">

        {/* Header */}
        <div className="ste-header">
          <h3>{editing ? `Edit Scenario "${editing.name}"` : 'New Scenario Template'}</h3>
          <button className="ste-close" onClick={onClose} type="button"><X size={18} /></button>
        </div>

        <div className="ste-body">
          {/* Name */}
          <div className="ste-name-row">
            <label>Template Name</label>
            <input
              className="ste-name-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Custom-3A"
            />
          </div>

          {/* Mode tabs */}
          <div className="ste-mode-tabs" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex' }}>
              <button
                className={`ste-mode-tab ${mode === 'manual' ? 'ste-mode-tab--active' : ''}`}
                onClick={() => setMode('manual')}
                type="button"
              >
                Manual
              </button>
              <button
                className={`ste-mode-tab ${mode === 'builder' ? 'ste-mode-tab--active' : ''}`}
                onClick={() => setMode('builder')}
                type="button"
              >
                Builder
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowReference(v => !v)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
                border: `1px solid ${showReference ? '#3b82f6' : 'var(--border)'}`,
                background: showReference ? 'rgba(59,130,246,0.12)' : 'transparent',
                color: showReference ? '#60a5fa' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {showReference ? <EyeOff size={13} /> : <Eye size={13} />}
              {showReference ? 'Hide' : 'View'} scenarios
            </button>
          </div>

          {showReference && (
            <div style={{
              border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 16,
              maxHeight: 220, overflowY: 'auto', background: 'var(--bg-app)',
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 10 }}>
                {activeDay
                  ? <>Scenarios highlighted where <strong style={{ color: '#60a5fa' }}>{activeDay}</strong> appears — for reference while building.</>
                  : 'Day structure of all available scenarios — for reference while building.'}
              </div>
              <ScenarioGrid
                names={allScenarioNames}
                scenarioDays={allBuiltInDays}
                allLabels={allBuiltInLabels}
                highlightDay={activeDay}
                compact
              />
            </div>
          )}

          {/* ── Manual mode ─────────────────────────────────────────────── */}
          {mode === 'manual' && (
            <div className="ste-manual">
              <div className="ste-day-tabs-row">
                {days.map((d, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`ste-day-tab ${activeDayIdx === idx ? 'ste-day-tab--active' : ''}`}
                    onClick={() => setActiveDayIdx(idx)}
                  >
                    {d}
                    <span
                      className="ste-day-tab-x"
                      onClick={ev => { ev.stopPropagation(); removeDay(d, idx); }}
                      title="Remove day"
                    >
                      <X size={10} />
                    </span>
                  </button>
                ))}
                {days.length < ALL_DAY_LABELS.length && (
                  <button type="button" className="ste-add-day" onClick={addDay}>
                    <Plus size={13} /> Add day
                  </button>
                )}
              </div>

              {activeDay !== null && activeDayIdx < days.length && (
                <>
                  {activeDay === 'MD' && (
                    <div className="ste-copy-row" style={{ alignItems: 'center' }}>
                      <span className="ste-copy-label">Match duration:</span>
                      {[45, 60, 90].map(m => (
                        <button
                          key={m}
                          type="button"
                          className="ste-copy-chip"
                          style={mdMinutes === m ? { borderColor: '#3b82f6', color: '#3b82f6' } : undefined}
                          onClick={() => handleMdMinutesChange(m)}
                        >
                          {m} min
                        </button>
                      ))}
                      <input
                        type="number"
                        min={1}
                        max={120}
                        value={mdMinutes}
                        onChange={e => handleMdMinutesChange(parseInt(e.target.value, 10) || 90)}
                        style={{ width: 56, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12 }}
                      />
                      <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>
                        min — scales automatically from the 90-min baseline.
                      </span>
                    </div>
                  )}

                  {/* Quick-copy from existing scenario — every known scenario, built-in and
                      custom, same universe Builder mode's "Copy targets from" dropdown offers.
                      Used to be capped to the first 10 names, but built-ins always fill that
                      before any custom one gets a turn, so a self-made scenario could never
                      appear here at all. The row wraps (see .ste-copy-row), so no cap is needed. */}
                  <div className="ste-copy-row">
                    <span className="ste-copy-label">Copy from:</span>
                    {allScenarioNames.map(sn => {
                      const hasSrcDay = (allBuiltInDays[sn] || []).includes(activeDay);
                      return (
                        <button
                          key={sn}
                          type="button"
                          className="ste-copy-chip"
                          disabled={!hasSrcDay}
                          onClick={() => copyFromScenario(activeDay, sn, activeDay)}
                          title={hasSrcDay ? `Copy ${activeDay} from ${sn}` : `${sn} has no ${activeDay}`}
                        >
                          {sn}
                        </button>
                      );
                    })}
                  </div>

                  <DayMetricsTable
                    targets={targets[activeDay]}
                    onChange={(pos, metric, bound, val) =>
                      updateTarget(activeDay, pos, metric, bound, val)
                    }
                  />
                </>
              )}
            </div>
          )}

          {/* ── Builder mode ─────────────────────────────────────────────── */}
          {mode === 'builder' && (
            <div className="ste-builder">
              <p className="ste-builder-desc">
                For each day in your new scenario, pick which existing scenario
                and day to copy the targets from.
              </p>

              {builderSlots.some(s => s.dayLabel === 'MD') && (
                <div className="ste-copy-row" style={{ alignItems: 'center', marginBottom: 12 }}>
                  <span className="ste-copy-label">Match duration:</span>
                  {[45, 60, 90].map(m => (
                    <button
                      key={m}
                      type="button"
                      className="ste-copy-chip"
                      style={mdMinutes === m ? { borderColor: '#3b82f6', color: '#3b82f6' } : undefined}
                      onClick={() => setMdMinutes(m)}
                    >
                      {m} min
                    </button>
                  ))}
                  <input
                    type="number"
                    min={1}
                    max={120}
                    value={mdMinutes}
                    onChange={e => setMdMinutes(parseInt(e.target.value, 10) || 90)}
                    style={{ width: 56, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12 }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>
                    min — planning form scales MD targets by (mins/90) + 10% warmup.
                  </span>
                </div>
              )}

              {builderSlots.map((slot, i) => (
                <BuilderSlot
                  key={i}
                  slot={slot}
                  index={i}
                  allBuiltInDays={allBuiltInDays}
                  allTemplates={allScenarioNames}
                  onChange={updateSlot}
                  onRemove={removeSlot}
                />
              ))}

              <button type="button" className="ste-add-slot" onClick={addBuilderSlot}>
                <Plus size={14} /> Add day slot
              </button>

              {builderSlots.length > 0 && (
                <p className="ste-builder-preview-label">Preview of resulting days:</p>
              )}
              {builderSlots.map((slot, i) => (
                <div key={i} className="ste-preview-row">
                  <span className="ste-preview-day">{slot.dayLabel}</span>
                  <span className="ste-preview-arrow">←</span>
                  <span className="ste-preview-src">{slot.sourceScenario} / {slot.sourceDay}</span>
                </div>
              ))}
            </div>
          )}

          {error && <div className="ste-error">{error}</div>}
        </div>

        {/* Footer */}
        <div className="ste-footer">
          <button type="button" className="ste-btn ste-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ste-btn ste-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
}
